import type { BreakdownVM, DonutSeg } from './types';

export const normalizePercent = (v: unknown): number => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
};

export const formatPercent = (v: number): string => {
  const rounded1 = Math.round(v * 10) / 10;
  return `${Number.isInteger(rounded1) ? rounded1.toFixed(0) : rounded1.toFixed(1)}%`;
};

export const buildBreakdownVMs = (
  breakdowns: { productTypeId: number; productId: number; percentageBreakdown: number }[],
  breakdownNameMap: Map<string, string>,
  productTypeNameFromIdFn: (id: number) => string
): BreakdownVM[] => {
  return (breakdowns ?? []).map((b, i) => {
    const typeId = Number(b?.productTypeId);
    const productId = Number(b?.productId);

    const typeName = productTypeNameFromIdFn(typeId);
    const productName =
      breakdownNameMap.get(`${typeId}-${productId}`) || `#${productId}`;

    return {
      key: `${typeId}-${productId}-${i}`,
      typeId,
      productId,
      typeName,
      productName,
      percentage: normalizePercent(b.percentageBreakdown),
    };
  });
};

const BASE_HUE = 215; // blue

const makeMonochromeScale = (n: number, hue = BASE_HUE): string[] => {
  const minL = 88;
  const maxL = 42;
  const minS = 18;
  const maxS = 62;

  if (n <= 1) return [`hsl(${hue}, ${maxS}%, ${maxL}%)`];

  return Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1); // 0..1
    const l = minL + (maxL - minL) * t;
    const s = minS + (maxS - minS) * t;
    return `hsl(${hue}, ${s.toFixed(0)}%, ${l.toFixed(0)}%)`;
  });
};

export const buildBreakdownColorMap = (items: BreakdownVM[]): Map<string, string> => {
  const sorted = [...items].sort((a, b) => b.percentage - a.percentage);

  const scale = makeMonochromeScale(Math.min(sorted.length, 8)).slice().reverse();

  const map = new Map<string, string>();
  sorted.forEach((x, idx) => {
    map.set(x.key, scale[Math.min(idx, scale.length - 1)]);
  });

  return map;
};

export const buildDonutSegments = (
  items: BreakdownVM[],
  colors: Map<string, string>
): { segs: DonutSeg[]; total: number } => {
  const total = items.reduce((s, x) => s + (x.percentage ?? 0), 0);
  if (!total) return { segs: [], total: 0 };

  const scale = total > 0 ? 100 / total : 1;

  const segs: DonutSeg[] = items.map((x) => ({
    key: x.key,
    percent: (x.percentage ?? 0) * scale,
    color: colors.get(x.key) ?? 'rgba(0,0,0,0.25)',
  }));

  const used = segs.reduce((s, x) => s + x.percent, 0);
  const remainder = Math.max(0, 100 - used);
  if (remainder > 0.3) {
    segs.push({ key: '__remainder__', percent: remainder, color: 'rgba(0,0,0,0.10)' });
  }

  return { segs, total };
};

